/**
 * The board socket.
 *
 * `GET /api/relay/boards/:id/socket`, same origin, so the `mauvely_session`
 * cookie rides the upgrade on its own. A guest passes the meeting token in the
 * query string because a browser cannot set a header on an upgrade.
 *
 * ── Local first, then the wire ──────────────────────────────────────────────
 * `apply` changes the local elements and emits `sync` before the frame leaves,
 * so a stroke follows the pointer that draws it. The server answers `ack` with
 * its `seq`; it never echoes an op back to its sender.
 *
 * ── A reconnect is a resync, because there is no op log ─────────────────────
 * `BoardDO` keeps the materialised elements, not the ops that made them, so
 * there is nothing to replay: the socket comes back, sends `hello` and is
 * handed the whole board. Ops sent but not yet acked are re-applied on top of
 * that answer and sent again — last write wins, which is the board's rule
 * everywhere else too.
 *
 * ── Events ─────────────────────────────────────────────────────────────────
 * `sync` fires on every change to the element set, whoever made it, and
 * carries the whole set; `op` fires as well for a change somebody else made,
 * for anything that wants to know who. A consumer that only draws needs
 * `sync` alone.
 *
 * ── Cursors and live strokes are coalesced ──────────────────────────────────
 * `hello` asks for `cursor`, `live` and `presence`, which the server relays and
 * never stores. Every one of them wakes the board's Durable Object, so this
 * client sends at most one of each per `EPHEMERAL_MS`: the first at once, then
 * the latest one when the interval is up. Nothing is sent while the pointer is
 * still, and nothing is queued across a reconnect.
 */

import type {
  BoardClient, BoardElement, BoardEvent, BoardEvents, BoardOp, BoardParty, RemoteOp
} from './types';

/** The server refuses a frame over 64 KiB. */
const MAX_FRAME_BYTES = 64 * 1024;

/** `{"t":"ping"}` / `{"t":"pong"}` is the auto-response pair the inbox socket
 *  registers, answered in the runtime without waking the object. 45s because
 *  Cloudflare drops an idle socket at 60. */
const HEARTBEAT_MS = 45_000;

const RETRY_BASE_MS = 500;
const RETRY_CAP_MS = 30_000;

/** About 30 a second: smooth enough to follow, few enough to keep the board's
 *  object from waking for every mouse event. */
export const EPHEMERAL_MS = 33;

/** A live element bigger than this is not sent, and the one already on
 *  everybody else's screen stays until the finished element replaces it. Half
 *  the frame cap, so the `add` that finishes the stroke still fits. */
export const MAX_LIVE_BYTES = MAX_FRAME_BYTES / 2;

/** The server's cap on a stroke's numbers, which a live stroke is held to as an
 *  `add` is. Past it the frame would come back as an alert. */
const MAX_POINTS = 8192;

/** What the client sends. */
type ClientFrame =
  | { t: 'hello'; presence: true }
  | { t: 'op'; id: string; op: BoardOp }
  | { t: 'cursor'; x: number; y: number }
  | { t: 'live'; el: BoardElement | null }
  | { t: 'ping' };

/** What the server sends. */
export type ServerFrame =
  | { t: 'sync'; seq: number; elements: BoardElement[] }
  | { t: 'op'; seq: number; by: string; at: number; op: BoardOp }
  | { t: 'ack'; id: string; seq: number }
  | { t: 'alert'; code: 'protocol' | 'unauthorized' | 'server'; message: string }
  | { t: 'presence'; you: string; parties: BoardParty[] }
  | { t: 'cursor'; by: string; x: number; y: number }
  | { t: 'live'; by: string; el: BoardElement | null };

/** The socket, as this file uses it. Everything the browser's `WebSocket`
 *  carries beyond these four lines is unused, and a test supplies its own. */
export interface BoardSocket {
  send(data: string): void;
  close(): void;
}

export interface SocketHandlers {
  open: () => void;
  message: (data: string) => void;
  /** With the close code, where the socket gave one. */
  close: (code?: number) => void;
  error: () => void;
}

/** relay-server's `CLOSE.roomChanged`: the board moved room. */
const ROOM_CHANGED = 4007;

export type BoardSocketFactory = (url: string, handlers: SocketHandlers) => BoardSocket;

const browserSocket: BoardSocketFactory = (url, handlers) => {
  const ws = new WebSocket(url);
  ws.addEventListener('open', () => handlers.open());
  ws.addEventListener('message', (ev: MessageEvent) => {
    if (typeof ev.data === 'string') handlers.message(ev.data);
  });
  ws.addEventListener('close', (ev: CloseEvent) => handlers.close(ev.code));
  ws.addEventListener('error', () => handlers.error());
  return {
    send: (data) => ws.send(data),
    close: () => ws.close()
  };
};

export interface BoardClientOptions {
  /** The page's origin. Given only by tests, which have no `window`. */
  origin?: string;
  open?: BoardSocketFactory;
  /** Ids for the op frames. The elements' own ids are minted by the caller. */
  frameId?: () => string;
}

/** `https://relay.mauvely.com` → `wss://relay.mauvely.com/api/relay/boards/…`. */
export function boardSocketUrl(origin: string, boardId: string, guestToken?: string): string {
  const url = new URL(`/api/relay/boards/${encodeURIComponent(boardId)}/socket`, origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  if (guestToken) url.searchParams.set('token', guestToken);
  return url.toString();
}

/**
 * One op against one element set.
 *
 * Exported because the canvas undoes by building the inverse op and the tests
 * check both halves against the same function.
 */
export function applyOp(elements: BoardElement[], op: BoardOp): BoardElement[] {
  switch (op.k) {
    case 'add': {
      const without = elements.filter((e) => e.id !== op.el.id);
      return [...without, op.el];
    }
    case 'update':
      return elements.map((e) => (e.id === op.id ? { ...e, ...op.patch } : e));
    case 'remove':
      return elements.filter((e) => e.id !== op.id);
    case 'clear':
      return [];
  }
}

/**
 * At most one send per interval: the first value at once, then the latest one
 * when the interval is up. Values in between are replaced, never queued — a
 * cursor that was somewhere 20 ms ago is of no use to anybody.
 */
export class Coalescer<T> {
  private last = -Infinity;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private pending: { value: T } | null = null;

  constructor(private readonly ms: number, private readonly send: (value: T) => void) {}

  push(value: T): void {
    this.pending = { value };
    if (this.timer !== null) return;
    const wait = this.last + this.ms - Date.now();
    if (wait <= 0) this.flush();
    else this.timer = setTimeout(() => this.flush(), wait);
  }

  /** Drops whatever is waiting. */
  cancel(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = null;
    this.pending = null;
  }

  private flush(): void {
    this.timer = null;
    const pending = this.pending;
    this.pending = null;
    if (!pending) return;
    this.last = Date.now();
    this.send(pending.value);
  }
}

export class RelayBoardClient implements BoardClient {
  private els: BoardElement[] = [];
  private seq = 0;
  private socket: BoardSocket | null = null;
  private boardId = '';
  private guestToken: string | undefined;
  private attempt = 0;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private beat: ReturnType<typeof setInterval> | null = null;
  /** Sent, not yet acked. Re-applied on top of a reconnect's `sync` and sent
   *  again, in the order they were made. */
  private pending = new Map<string, BoardOp>();
  private done = false;
  private opened: ((value: void) => void) | null = null;
  /** `unknown` for the reason lib/calls/session.ts states: the set holds
   *  callbacks for differently-shaped events. */
  private handlers = new Map<BoardEvent, Set<unknown>>();
  /** Between the socket opening and closing. A browser socket throws on a send
   *  before it opens, and a cursor is not worth holding until it does. */
  private ready = false;
  private readonly cursors = new Coalescer<{ x: number; y: number }>(EPHEMERAL_MS, (p) =>
    this.sendEphemeral({ t: 'cursor', x: p.x, y: p.y })
  );
  private readonly lives = new Coalescer<BoardElement>(EPHEMERAL_MS, (el) =>
    this.sendEphemeral({ t: 'live', el })
  );

  constructor(private readonly opts: BoardClientOptions = {}) {}

  connect(boardId: string, guestToken?: string): Promise<void> {
    this.boardId = boardId;
    this.guestToken = guestToken;
    this.done = false;
    const first = new Promise<void>((resolve) => {
      this.opened = resolve;
    });
    this.dial();
    return first;
  }

  elements(): BoardElement[] {
    return this.els;
  }

  apply(op: BoardOp): void {
    const id = this.opts.frameId ? this.opts.frameId() : crypto.randomUUID();
    const frame = JSON.stringify({ t: 'op', id, op } satisfies ClientFrame);

    if (frame.length > MAX_FRAME_BYTES) {
      this.emit('status', 'error', 'That is too big to draw in one piece.');
      return;
    }

    this.els = applyOp(this.els, op);
    this.pending.set(id, op);
    this.emit('sync', this.els, this.seq);
    this.socket?.send(frame);
  }

  cursor(x: number, y: number): void {
    if (this.ready) this.cursors.push({ x, y });
  }

  live(el: BoardElement | null): void {
    if (!this.ready) return;
    if (el) {
      if ((el.points?.length ?? 0) <= MAX_POINTS) this.lives.push(el);
      return;
    }
    // The end of a gesture goes at once, and takes anything still waiting with
    // it: a stroke frame sent after it would put the stroke back.
    this.lives.cancel();
    this.sendEphemeral({ t: 'live', el: null });
  }

  on<E extends BoardEvent>(event: E, cb: BoardEvents[E]): () => void {
    let set = this.handlers.get(event);
    if (!set) {
      set = new Set();
      this.handlers.set(event, set);
    }
    set.add(cb);
    return () => set.delete(cb);
  }

  close(): void {
    this.done = true;
    this.stopTimers();
    this.socket?.close();
    this.socket = null;
    this.emit('status', 'closed', undefined);
    this.handlers.clear();
  }

  private dial(): void {
    const origin = this.opts.origin ?? window.location.origin;
    const url = boardSocketUrl(origin, this.boardId, this.guestToken);
    this.emit('status', 'connecting', undefined);

    const open = this.opts.open ?? browserSocket;
    this.socket = open(url, {
      open: () => this.onOpen(),
      message: (data) => this.onMessage(data),
      close: (code) => this.onClose(code),
      error: () => this.emit('status', 'error', 'The board went quiet.')
    });
  }

  private onOpen(): void {
    this.ready = true;
    this.send({ t: 'hello', presence: true });
    this.beat = setInterval(() => this.send({ t: 'ping' }), HEARTBEAT_MS);
  }

  private onMessage(data: string): void {
    // The heartbeat's answer is handled in the runtime and is not a frame.
    if (data === '{"t":"pong"}') return;

    const frame = parseFrame(data);
    if (!frame) return;

    switch (frame.t) {
      case 'sync':
        this.onSync(frame.elements, frame.seq);
        break;
      case 'op':
        this.onRemote(frame);
        break;
      case 'ack':
        this.pending.delete(frame.id);
        this.seq = frame.seq;
        break;
      case 'alert':
        // Nothing this client can do about `unauthorized` changes on a retry,
        // and a board that reconnects forever is a board nobody is told about.
        if (frame.code === 'unauthorized') this.done = true;
        this.emit('status', 'error', frame.message);
        break;
      case 'presence':
        this.emit('presence', frame.parties, frame.you);
        break;
      case 'cursor':
        this.emit('cursor', frame.by, frame.x, frame.y);
        break;
      case 'live':
        this.emit('live', frame.by, frame.el);
        break;
    }
  }

  private onSync(elements: BoardElement[], seq: number): void {
    this.attempt = 0;
    this.seq = seq;
    this.els = elements;

    // Re-send what the old socket never got an ack for, in order.
    for (const [id, op] of this.pending) {
      this.els = applyOp(this.els, op);
      this.send({ t: 'op', id, op });
    }

    this.emit('sync', this.els, this.seq);
    this.emit('status', 'open', undefined);
    this.opened?.();
    this.opened = null;
  }

  private onRemote(frame: Extract<ServerFrame, { t: 'op' }>): void {
    this.seq = frame.seq;
    this.els = applyOp(this.els, frame.op);
    const remote: RemoteOp = { seq: frame.seq, by: frame.by, at: frame.at, op: frame.op };
    this.emit('op', remote);
    this.emit('sync', this.els, this.seq);
  }

  private onClose(code?: number): void {
    this.stopTimers();
    this.socket = null;
    if (this.done) return;
    if (code === ROOM_CHANGED) {
      // Not a fault: dial again at once, through the new room's check.
      this.attempt = 0;
      this.emit('moved');
    }

    this.emit('status', 'connecting', undefined);
    const delay = Math.min(RETRY_CAP_MS, RETRY_BASE_MS * 2 ** this.attempt);
    this.attempt += 1;
    // A jittered delay, so twenty boards reopened by one deploy do not arrive
    // together.
    this.retry = setTimeout(() => this.dial(), delay * (0.8 + Math.random() * 0.4));
  }

  private stopTimers(): void {
    this.ready = false;
    this.cursors.cancel();
    this.lives.cancel();
    if (this.retry !== null) clearTimeout(this.retry);
    if (this.beat !== null) clearInterval(this.beat);
    this.retry = null;
    this.beat = null;
  }

  private send(frame: ClientFrame): void {
    this.socket?.send(JSON.stringify(frame));
  }

  private sendEphemeral(frame: ClientFrame): void {
    const text = JSON.stringify(frame);
    if (text.length > MAX_LIVE_BYTES) return;
    if (this.ready) this.socket?.send(text);
  }

  private emit<E extends BoardEvent>(event: E, ...args: Parameters<BoardEvents[E]>): void {
    for (const cb of this.handlers.get(event) ?? []) {
      (cb as (...a: Parameters<BoardEvents[E]>) => void)(...args);
    }
  }
}

/** A frame the client understands, or null. A malformed frame is dropped: the
 *  socket carries other people's drawing, and one bad message is not a reason
 *  to tear the board down. */
export function parseFrame(data: string): ServerFrame | null {
  let value: unknown;
  try {
    value = JSON.parse(data);
  } catch {
    return null;
  }

  if (typeof value !== 'object' || value === null) return null;
  const frame = value as Partial<ServerFrame> & { t?: unknown };

  switch (frame.t) {
    case 'sync':
      return Array.isArray((frame as { elements?: unknown }).elements)
        ? (frame as Extract<ServerFrame, { t: 'sync' }>)
        : null;
    case 'op':
      return (frame as { op?: unknown }).op ? (frame as Extract<ServerFrame, { t: 'op' }>) : null;
    case 'ack':
      return typeof (frame as { id?: unknown }).id === 'string'
        ? (frame as Extract<ServerFrame, { t: 'ack' }>)
        : null;
    case 'alert':
      return frame as Extract<ServerFrame, { t: 'alert' }>;
    case 'presence': {
      const f = frame as { you?: unknown; parties?: unknown };
      return typeof f.you === 'string' && Array.isArray(f.parties)
        ? (frame as Extract<ServerFrame, { t: 'presence' }>)
        : null;
    }
    case 'cursor': {
      const f = frame as { by?: unknown; x?: unknown; y?: unknown };
      return typeof f.by === 'string' && Number.isFinite(f.x) && Number.isFinite(f.y)
        ? (frame as Extract<ServerFrame, { t: 'cursor' }>)
        : null;
    }
    case 'live': {
      const f = frame as { by?: unknown; el?: unknown };
      return typeof f.by === 'string' && (f.el === null || typeof f.el === 'object')
        ? (frame as Extract<ServerFrame, { t: 'live' }>)
        : null;
    }
    default:
      return null;
  }
}

export const openBoard = (opts?: BoardClientOptions): BoardClient => new RelayBoardClient(opts);
